<template>
  <teleport to="body">
    <div v-if="visible" class="catalogo-overlay">
      <div class="catalogo-modal">
        <div class="catalogo-header">
          <h3>Insertar bloque de datos</h3>
          <button class="btn-cerrar" @click="emit('cerrar')">✕</button>
        </div>
        <div class="catalogo-body">

          <!-- Paso 1: seleccionar tipo -->
          <template v-if="paso === 1">
            <p class="catalogo-desc">Seleccione el tipo de bloque que desea insertar en la sección actual.</p>
            <div class="catalogo-grid">
              <button
                v-for="t in tipos"
                :key="t.id"
                class="tipo-card"
                :class="{ seleccionado: tipoSel === t.id }"
                @click="seleccionarTipo(t.id)"
              >
                <span class="tipo-nombre">{{ t.nombre }}</span>
                <span class="tipo-desc">{{ t.descripcion }}</span>
              </button>
            </div>
          </template>

          <!-- Paso 2: seleccionar factor (solo GRAFICO_FACTOR) -->
          <template v-else-if="paso === 2">
            <button class="btn-back" @click="paso = 1">← Volver</button>
            <p class="catalogo-desc">Seleccione el factor para el gráfico.</p>
            <div class="catalogo-lista-factores">
              <button
                v-for="f in factores"
                :key="f.id_componente"
                class="factor-item"
                :class="{ seleccionado: factorSel === f.id_componente }"
                @click="factorSel = f.id_componente"
              >
                <b>{{ fmtOrdinal(f.ordinal) }}</b> {{ f.nombre }}
              </button>
            </div>
          </template>

        </div>
        <div class="catalogo-footer">
          <button class="btn salir" @click="emit('cerrar')">Cancelar</button>
          <button class="btn guardar" :disabled="!puedeInsertar" @click="confirmar">Insertar</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
/**
 * Catálogo de bloques reutilizables del informe.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed } from 'vue'
import { useGestionResultadosAutoevaluacion } from '@/store/GestionResultadosAutoevaluacion'

defineProps<{ visible: boolean }>()
const emit = defineEmits<{
  (e: 'cerrar'): void
  (e: 'insertar', bloque: { tipo: string; params?: Record<string, any> }): void
}>()

const store = useGestionResultadosAutoevaluacion()

function sortByOrdinal(a: string | null | undefined, b: string | null | undefined): number {
  const ap = (a ?? '').split('.').map(n => parseInt(n, 10) || 0)
  const bp = (b ?? '').split('.').map(n => parseInt(n, 10) || 0)
  for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
    const d = (ap[i] ?? 0) - (bp[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

const factores = computed(() =>
  [...(store.resultados?.factores ?? [])].sort((a, b) => sortByOrdinal(a.ordinal, b.ordinal))
)

function fmtOrdinal(ordinal: string | null | undefined): string {
  if (!ordinal) return ''
  const parts = ordinal.split('.')
  return parts.length <= 1 ? ordinal : parts.slice(1).join('.')
}

const tipos = [
  { id: 'TEXTO',            nombre: 'Caja de texto',         descripcion: 'Párrafo de texto libre con formato (negrita, listas, títulos…). Puede ir antes o después de tablas.' },
  { id: 'KPIS_RESUMEN',     nombre: 'KPIs de resumen',       descripcion: 'Cuadro de métricas clave: total indicadores, promedio y nivel general.' },
  { id: 'TABLA_GENERAL',    nombre: 'Tabla general',         descripcion: 'Tabla completa con todos los factores, características, aspectos y valoraciones.' },
  { id: 'TABLA_SEMAFORO',   nombre: 'Tabla semáforo',        descripcion: 'Vista de clasificación por fortalezas, debilidades y aspectos muy débiles.' },
  { id: 'TABLA_ESTRUCTURA', nombre: 'Estructura jerárquica', descripcion: 'Tabla que muestra la jerarquía completa del modelo con valoraciones.' },
  { id: 'GRAFICOS_TODOS',   nombre: 'Todos los gráficos',    descripcion: 'Gráficos de barras de todos los factores del modelo.' },
  { id: 'GRAFICO_FACTOR',   nombre: 'Gráfico por factor',    descripcion: 'Gráfico de barras de un factor específico del modelo.' },
  { id: 'COMPARATIVO',      nombre: 'Comparativo',           descripcion: 'Vista comparativa de calificaciones entre distintos procesos de autoevaluación.' },
  { id: 'IMAGEN',           nombre: 'Imagen',                descripcion: 'Inserta una imagen desde URL. Configurable en tamaño y pie de imagen.' },
  { id: 'TABLA_CUSTOM',     nombre: 'Tabla personalizada',   descripcion: 'Crea una tabla libre con columnas y filas propias, con opción de importar datos del modelo.' },
]

const paso = ref<1 | 2>(1)
const tipoSel = ref<string | null>(null)
const factorSel = ref<number | null>(null)

function seleccionarTipo(id: string) {
  tipoSel.value = id
  if (id === 'GRAFICO_FACTOR') {
    paso.value = 2
    factorSel.value = null
  }
}

const puedeInsertar = computed(() => {
  if (!tipoSel.value) return false
  if (tipoSel.value === 'GRAFICO_FACTOR') return factorSel.value !== null
  return true
})

function confirmar() {
  if (!tipoSel.value || !puedeInsertar.value) return
  const bloque: { tipo: string; params?: Record<string, any> } = { tipo: tipoSel.value }
  if (tipoSel.value === 'GRAFICO_FACTOR' && factorSel.value !== null) {
    bloque.params = { id_componente: factorSel.value }
  }
  emit('insertar', bloque)
  tipoSel.value = null
  factorSel.value = null
  paso.value = 1
}
</script>

<style scoped>
.catalogo-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 2000;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.catalogo-modal {
  background: white; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  width: 100%; max-width: 680px; max-height: 90vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.catalogo-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid #e0e0e0;
}
.catalogo-header h3 { margin: 0; font-size: 1rem; font-weight: 700; color: #1a1a1a; }
.btn-cerrar {
  background: none; border: none; font-size: 1.1rem; cursor: pointer;
  color: #888; line-height: 1; padding: 0; transition: color 0.15s;
}
.btn-cerrar:hover { color: #dc3545; }

.catalogo-body { padding: 1.25rem; overflow-y: auto; flex: 1; }
.catalogo-desc { font-size: 0.85rem; color: #555; margin: 0 0 1rem; }

.catalogo-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 0.75rem;
}
.tipo-card {
  display: flex; flex-direction: column; gap: 0.3rem; align-items: flex-start;
  border: 2px solid #e0e0e0; border-radius: 8px; padding: 0.9rem 1rem;
  background: white; cursor: pointer; text-align: left; transition: all 0.15s;
  font-family: inherit;
}
.tipo-card:hover { border-color: #0e69a0; background: #f5faff; }
.tipo-card.seleccionado { border-color: #0e69a0; background: #eaf3fb; }
.tipo-nombre { font-size: 0.88rem; font-weight: 700; color: #1a1a1a; }
.tipo-desc { font-size: 0.75rem; color: #777; line-height: 1.3; }

.btn-back {
  background: none; border: none; color: #0e69a0; font-weight: 600; font-size: 0.88rem;
  cursor: pointer; padding: 0; margin-bottom: 0.75rem; font-family: inherit;
}
.btn-back:hover { text-decoration: underline; }

.catalogo-lista-factores { display: flex; flex-direction: column; gap: 0.4rem; }
.factor-item {
  padding: 0.6rem 0.9rem; border: 2px solid #e0e0e0; border-radius: 8px;
  background: white; cursor: pointer; text-align: left; font-size: 0.88rem;
  font-family: inherit; transition: all 0.15s;
}
.factor-item:hover { border-color: #0e69a0; background: #f5faff; }
.factor-item.seleccionado { border-color: #0e69a0; background: #eaf3fb; }
.factor-item b { color: #0e69a0; margin-right: 0.4em; }

.catalogo-footer {
  display: flex; gap: 0.75rem; justify-content: flex-end;
  padding: 0.9rem 1.25rem; border-top: 1px solid #e0e0e0;
}

.btn {
  padding: 0.55rem 1.2rem; font-weight: 600; border: none; border-radius: 8px;
  cursor: pointer; transition: all 0.15s ease; font-family: inherit; font-size: 0.9rem;
}
.btn.salir { background: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background: #f5f5f5; }
.btn.guardar { background: #eea222; color: #1a1a1a; }
.btn.guardar:hover:not(:disabled) { background: #0e69a0; color: white; }
.btn.guardar:disabled { opacity: 0.45; cursor: not-allowed; }
</style>
